import { axiosArisChatInstance } from "../AxiosConfiguration";

/**
 * Discovery / Element conversation entry (PB-12).
 *
 * Direct to aris-ai — `discovery_router` is not proxied by core-lambda, same
 * posture as Services/dialogues.js and Services/faithProfile.js.
 */

/**
 * `POST /ARIS/v4/discovery/elements/{name}/talk` — enter an Element
 * conversation.
 *
 * Two calls, by design (Foundational Self-Knowledge v26.1, "Conversation
 * Entry"; `plans/12` §9.1): selecting **Talk with Aris** is a choice, not a
 * launch. Called with `mode: null` the endpoint answers the choice —
 *
 *   { requires_mode_choice: true,
 *     question_key: "element.talk.how_would_you_like_to_continue",
 *     options: [ { mode: "VOICE"|"TEXT", label_key, hint_key, recommended? } ] }
 *
 * Called again with the chosen mode it resolves the entry —
 *
 *   { conversation_id, mode,
 *     opening,                       // applied, member-specific; never generic
 *     entry_context: { entry_type, element_id, domain_id, selected_element,
 *                      element_level, related_elements, element_understanding },
 *     context_included: [...] }
 *
 * `remember_mode` stores the choice server-side as
 * `preferences.preferred_modality` — a soft default the next entry can
 * pre-fill, never a lock (the endpoint still returns the choice every time).
 *
 * `name` must be the canonical registry name, capitalised exactly ("Grounded",
 * not "grounded") — the registry lookup is case-sensitive and 404s otherwise.
 * The elements payload's `displayName` is that name.
 *
 * `sessionId` is the chat session the member is about to talk in, and it is what
 * makes the opening real rather than decorative. Conversation history is
 * server-side: pass the session and aris-ai stores the opening as that
 * conversation's first assistant message, so the model holds the question the
 * member's first reply answers. Omit it and the opening exists only in this
 * client's transcript — the member reads "what made it stick?", answers "the
 * early mornings", and the model receives that as an opening line with no
 * referent.
 */
export const enterElementConversation = async (
  name,
  { mode = null, rememberMode = false, sessionId = null } = {}
) => {
  const res = await axiosArisChatInstance.post(
    `/ARIS/v4/discovery/elements/${encodeURIComponent(name)}/talk`,
    { mode, remember_mode: rememberMode, session_id: sessionId }
  );
  // aris-ai returns this payload unwrapped (no {payload:…} envelope).
  return res?.data?.payload ?? res?.data ?? null;
};

export default enterElementConversation;
